import {
  createEmailAttachmentDraft,
  type EmailAttachmentDraft,
  type UpdateEmailAttachmentDraft,
} from '@/hooks/email-attachment-draft';
import type {
  BriefTemplateContent,
  BulkInvitationDeliveryMode,
  CollabDeliveryMode,
  CollabDeliveryStatus,
  CreateInvitationResponse,
  Creator,
  CreatorUgcOffer,
  PayloadLocale,
  SearchAffiliateUsersResponse,
  WishlistCreator,
} from '@fluenceur/types';
import { createStore } from 'zustand';
import type { InvitableProgramResponse } from '@/types/affiliate-programs';
import type { PreparedInvitation } from '@/types/invitation';
import type { CollabType } from '@/components/actions/send-invitation/collab-types';

// Define possible views. `typeSelection` is the optional first step used only by
// the "propose a collaboration" entry (contact card); the other entries seed a
// later view directly and never see it. The `collab*` views are the sponsored
// and UGC paths of the propose-collab wizard, one set of steps for both.
export type InvitationView =
  | 'typeSelection'
  | 'inputSelection'
  | 'programSelection'
  | 'invitationChannel'
  | 'invitationDetails'
  | 'collabBrief'
  | 'collabDraft'
  | 'collabTerms'
  | 'collabSend'
  | 'collabDelivery'
  | 'collabDone'
  | 'bulkProgress';

/** How the sponsored brief starts: from a library template or a blank draft. */
export type CollabBriefMode = 'template' | 'blank';

/**
 * Delivery channel explicitly picked on the invitation channel step (mono
 * affiliation). Frontend-only: the backend still receives its existing channels
 * (DM/comment through /send, the outreach email endpoint) or nothing at all for
 * the shareable link. Deliberately NOT the persisted `CollabDeliveryMode`.
 */
export type InvitationChannelMode = 'link' | 'email' | 'dm' | 'comment';

/**
 * Pre-fill payload for a "resend from an existing collab" entry (drawer:
 * repropose / correct-and-resend / manual resend). It carries exactly the
 * sponsored terms the wizard needs to open on the `collabTerms` step with a
 * blank brief already seeded from the previous collab's frozen content. The new
 * proposal is a fresh send (bulk endpoint); the old collab stays immutable.
 */
export interface CollabReseedSeed {
  recipientIdentityId: string;
  /** Recipient display fields, so the send step resolves the mono line without
   * a full Creator object (the drawer only knows the collab's recipient ref). */
  recipientDisplayName: string;
  recipientAvatarUrl: string | null;
  recipientEmail: string | null;
  /** Frozen brief name reused for the new proposal. */
  name: string;
  content: BriefTemplateContent;
  respondByDays: number;
  /**
   * The unfunded brief this draft re-proposes, when that is what the entry is.
   * The server allows one re-proposal per unfunded brief and counts them by it,
   * so a draft that forgot where it came from would be counted as a first
   * attempt for ever. Null on every other resend entry.
   */
  reproposedFromCollabId: string | null;
}

/** One per-recipient outcome of the bulk send, ready for the done screen. */
export interface CollabSendResultLine {
  displayName: string;
  email: string | null;
  status: 'sent' | 'failed';
  lookupKey: string | null;
  /** Backend ErrorCode for a failed line (localizable known codes). */
  code: string | null;
  /** Backend English message for a failed line. */
  message: string | null;
  /**
   * Planned/attempted delivery outcome for this recipient. Absent
   * until the backend surfaces it on the bulk result — the done-step badge is
   * defensive and only renders when present.
   */
  deliveryStatus?: CollabDeliveryStatus;
  /** Failure cause when `deliveryStatus` is `failed`. */
  deliveryErrorCode?: string | null;
  /** The collab created for this line, when one was; it opens the tracking detail. */
  collabId?: string | null;
}

/** One item the bulk POST skipped at prepare time (no email, already invited…). */
export interface BulkSkippedItem {
  wishlistItemId: string;
  /** English reason, the recap's fallback when the code has no translation */
  reason: string;
  /** Skip reason as a translatable code; the recap prefers it over `reason` */
  reasonCode?: string | null;
}

/**
 * What the bulk send POST hands to the epilogue (`bulkProgress`) recap: the real
 * batch id (for the items GET), the delivery mode (drives per-line status) and
 * the items the prepare step skipped (unioned with the created items for the
 * recap). Seeded once at send; never read from the progress store, which
 * `clearBatch` wipes.
 */
export interface BulkProgressContext {
  batchId: string;
  deliveryMode: BulkInvitationDeliveryMode;
  skippedItems: BulkSkippedItem[];
}

// Define invitation types
export type InvitationType = 'creator' | 'affiliate' | 'email' | 'wishlist-bulk';
export type InvitationEntryIntent = 'standard' | 'affiliate-add';

/** The picked affiliate's identity lives on `target`; only the editable fields sit here. */
export interface AffiliateAddDraft {
  firstName: string;
  discountCode: string;
}

type SearchAffiliateUser = SearchAffiliateUsersResponse['data'][0];

/**
 * Data for a raw-email invitation target: the invite resolves off the email.
 */
export interface EmailInvitationData {
  email: string;
}

// Define the target for invitation
export type InvitationTarget =
  | { type: 'creator'; data: Creator }
  | { type: 'affiliate'; data: SearchAffiliateUser }
  | { type: 'email'; data: EmailInvitationData }
  | { type: 'wishlist-bulk'; data: WishlistCreator[] };

export interface InvitationToAProgram extends CreateInvitationResponse {
  programName?: string;
  loading: boolean;
  error: boolean;
  invitationMethods?: PreparedInvitation;
}

export type MessageLanguage = PayloadLocale;

export interface InvitationState {
  entryIntent: InvitationEntryIntent;
  affiliateAddDraft: AffiliateAddDraft;

  // Target can be creator, affiliate, or email data
  target: InvitationTarget | null;
  invitationType: InvitationType | null;

  // Legacy creator field for backward compatibility
  creator: Creator | null;

  programs: InvitableProgramResponse[];
  selectedProgram: Partial<InvitableProgramResponse>;
  invitationData: InvitationToAProgram | null;

  // Brand-known email for the creator being invited (propose-collab entry). When
  // set, the invitation is created with this address so the EMAIL outreach path
  // can reach the creator. `null` when the brand has no email on file.
  recipientEmail: string | null;
  emailAttachmentDraft: EmailAttachmentDraft;
  updateEmailAttachmentDraft: UpdateEmailAttachmentDraft;
  message: string;
  messageSnapshot: string | null; // Lexical JSON editor state
  compiledMessage: string; // Compiled plain text
  originalTemplate: string | null; // Original template for reset
  loading: boolean;
  error: boolean;
  currentView: InvitationView;

  // Delivery channel picked on the invitation channel step (mono affiliation).
  // Null until the step commits a choice or when the flow has no channel step
  // (bulk). Drives the mono delivery step's routing and banner.
  invitationChannelMode: InvitationChannelMode | null;

  // Recap payload for the bulk epilogue, seeded once by the send. Null outside a
  // bulk send (the recap view reads it instead of the progress store, which is
  // cleared on completion).
  bulkProgress: BulkProgressContext | null;

  // Collaboration type chosen on the type step (null until picked). Persisted so
  // a back-navigation to the type step restores the brand's choice.
  collabType: CollabType | null;

  // ── Sponsored-collab wizard state ──────────────────────────────────────────
  // Resolved identity of the mono recipient (contact-card entry). Bulk entries
  // resolve per line in the wizard instead.
  collabRecipientIdentityId: string | null;
  // Brief provenance: library template or blank draft. Null until the brief
  // step commits a choice.
  collabBriefMode: CollabBriefMode | null;
  collabTemplateId: string | null;
  // Collab name (template name, or the localized blank-brief name).
  collabName: string;
  // The working brief content shared by the draft/terms/send steps.
  collabContent: BriefTemplateContent | null;
  respondByDays: number;
  // Blank-draft toggle: also persist the brief as a library template on send.
  saveAsTemplate: boolean;
  // The compensation must be an explicit choice before sending: true once the
  // brand picks a type on the terms step, or when the chosen template already
  // carries a deliberate compensation (fixed with amount / gifted with product).
  compensationConfirmed: boolean;
  // Delivery mode explicitly picked on the wizard's channel step. Null until the
  // step commits a choice; drives the delivery step and the done screen.
  collabDeliveryMode: CollabDeliveryMode | null;
  // Per-recipient outcome of the bulk send; feeds the done screen.
  collabSendResults: CollabSendResultLine[] | null;

  // The unfunded brief this send re-proposes (drawer entry). Travels with the
  // send so the server can count the one re-proposal it allows. Null everywhere
  // else, including a first-time send.
  collabReproposedFromCollabId: string | null;

  // The brief application this send answers (Candidatures entry). Travels with
  // the send so the application turns "proposed" exactly when the proposal
  // leaves. Null for every other entry.
  collabBriefApplicationId: string | null;

  // "Resend from an existing collab" recipient (drawer entry): the mono
  // recipient's display fields, so the send step resolves the line without a
  // full Creator object. Null for every other entry.
  collabReseedRecipient: {
    displayName: string;
    avatarUrl: string | null;
    email: string | null;
  } | null;

  // Mono recipient display for a propose-collab entry whose contact has an
  // identity but NO social profile (never linked to Instagram/TikTok), so no
  // Creator object exists. Seeds the sponsored wizard's recipient line. Null for
  // every entry that carries a full Creator (or the reseed display above).
  collabRecipientDisplay: {
    displayName: string;
    avatarUrl: string | null;
  } | null;

  // The UGC offer of the mono recipient, while her badge is active: it
  // preselects UGC on the type step and sets the starting price beside the
  // amount. Null for a creator who offers none and for every bulk entry.
  collabRecipientUgcOffer: CreatorUgcOffer | null;

  // Email-invitation language override (undefined = use brand default)
  messageLanguage?: MessageLanguage;

  // Provenance tag for analytics + backend audit (e.g. 'agent_recommendation').
  // Undefined when invitation flow is opened from generic UI surfaces.
  source?: string;
  sourceRecommendationId?: string;

  // Actions
  setEntryIntent: (intent: InvitationEntryIntent) => void;
  setAffiliateAddDraft: (draft: AffiliateAddDraft) => void;
  setTarget: (target: InvitationTarget) => void;
  setCreator: (creator: Creator, sourceRecommendationId?: string) => void; // Legacy support
  setPrograms: (programs: InvitableProgramResponse[]) => void;
  setSelectedProgram: (program: Partial<InvitableProgramResponse>) => void;
  setInvitationData: (data: InvitationToAProgram | null) => void;
  setRecipientEmail: (email: string | null) => void;
  updateInvitationMethods: (data: PreparedInvitation) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (hasError: boolean) => void;
  setCurrentView: (view: InvitationView) => void;
  setInvitationChannelMode: (mode: InvitationChannelMode | null) => void;
  setBulkProgress: (context: BulkProgressContext | null) => void;
  setCollabType: (collabType: CollabType) => void;
  /** Select a library brief on the brief step before any content is seeded. */
  preselectCollabTemplate: (templateId: string) => void;
  setCollabRecipientIdentityId: (id: string | null) => void;
  /** Commit the brief step choice and seed the working content. */
  setCollabBrief: (choice: {
    mode: CollabBriefMode;
    templateId: string | null;
    name: string;
    content: BriefTemplateContent;
    compensationConfirmed: boolean;
  }) => void;
  updateCollabContent: (updater: (prev: BriefTemplateContent) => BriefTemplateContent) => void;
  setRespondByDays: (days: number) => void;
  setSaveAsTemplate: (value: boolean) => void;
  setCompensationConfirmed: (value: boolean) => void;
  setCollabDeliveryMode: (mode: CollabDeliveryMode) => void;
  setCollabSendResults: (results: CollabSendResultLine[]) => void;
  setCollabReproposedFromCollabId: (collabId: string | null) => void;
  setCollabBriefApplicationId: (applicationId: string | null) => void;
  setCollabReseedRecipient: (
    recipient: { displayName: string; avatarUrl: string | null; email: string | null } | null
  ) => void;
  setCollabRecipientDisplay: (
    recipient: { displayName: string; avatarUrl: string | null } | null
  ) => void;
  setCollabRecipientUgcOffer: (offer: CreatorUgcOffer | null) => void;
  setMessage: (message: string) => void;
  setMessageSnapshot: (snapshot: string) => void;
  setCompiledMessage: (message: string) => void;
  setOriginalTemplate: (template: string) => void;
  resetMessage: () => void;
  aiPersonalize: boolean;
  setAiPersonalize: (value: boolean) => void;
  setMessageLanguage: (language: MessageLanguage | undefined) => void;
  setSource: (source: string | undefined) => void;
  setSourceRecommendationId: (sourceRecommendationId: string | undefined) => void;
}

// We'll use this for initial state and reset
const getInitialState = () => ({
  entryIntent: 'standard' as InvitationEntryIntent,
  affiliateAddDraft: {
    firstName: '',
    discountCode: '',
  },
  target: null,
  invitationType: null,
  creator: null,
  programs: [],
  selectedProgram: {},
  invitationData: null,
  recipientEmail: null,
  loading: false,
  error: false,
  currentView: 'inputSelection' as InvitationView,
  invitationChannelMode: null as InvitationChannelMode | null,
  bulkProgress: null as BulkProgressContext | null,
  collabType: null,
  collabRecipientIdentityId: null,
  collabBriefMode: null as CollabBriefMode | null,
  collabTemplateId: null,
  collabName: '',
  collabContent: null,
  respondByDays: 7,
  // Default-on for the blank-brief path, whose draft step renders the toggle.
  // Any path that SKIPS the draft step must opt out explicitly (the reseed
  // seed does); the template path ignores the flag entirely.
  saveAsTemplate: true,
  compensationConfirmed: false,
  collabDeliveryMode: null as CollabDeliveryMode | null,
  collabSendResults: null,
  collabReproposedFromCollabId: null,
  collabBriefApplicationId: null,
  collabReseedRecipient: null,
  collabRecipientDisplay: null,
  collabRecipientUgcOffer: null,
  emailAttachmentDraft: createEmailAttachmentDraft(),
  message: '',
  messageSnapshot: null,
  compiledMessage: '',
  originalTemplate: null,
  aiPersonalize: false,
  messageLanguage: undefined,
  source: undefined,
  sourceRecommendationId: undefined,
});

const handleSetInvitationData = (
  set: (
    partial: Partial<InvitationState> | ((state: InvitationState) => Partial<InvitationState>)
  ) => void,
  invitationData: InvitationToAProgram | null
) => {
  set({
    invitationData,
    // Every affiliation target now passes through the explicit channel step:
    // mono picks link/email/dm/comment, bulk picks link/email (symmetry). The
    // step commits a mode, then routes to the delivery surface.
    currentView: 'invitationChannel',
    loading: false,
  });
};

const shouldDefaultAiPersonalization = (target: InvitationTarget) =>
  target.type === 'wishlist-bulk';

/**
 * Per-instance store factory. Each InvitationProvider creates its own store so
 * co-mounted modals (e.g. the global contact-drawer instance and a page-level
 * bulk instance) can never clobber each other's seed — with the old module
 * singleton, the last seed won and a send could target the wrong recipient.
 */
export const createInvitationStore = () =>
  createStore<InvitationState>((set) => ({
    ...getInitialState(),
    setEntryIntent: (entryIntent) =>
      set({
        entryIntent,
        // The Add affiliate surface opens with mono personalization enabled,
        // while generic contact/reproposal entries retain their opt-in default.
        aiPersonalize: entryIntent === 'affiliate-add',
      }),
    setAffiliateAddDraft: (affiliateAddDraft) => set({ affiliateAddDraft }),
    setTarget: (target) =>
      set((state) => ({
        target,
        invitationType: target.type,
        aiPersonalize:
          state.entryIntent === 'affiliate-add' || shouldDefaultAiPersonalization(target),
        // Set creator for backward compatibility
        creator: target.type === 'creator' ? target.data : null,
        currentView: 'programSelection',
      })),
    setCreator: (creator, sourceRecommendationId) =>
      set({
        creator,
        target: { type: 'creator', data: creator },
        invitationType: 'creator',
        aiPersonalize: false,
        currentView: 'programSelection',
        sourceRecommendationId,
      }),
    setPrograms: (programs) => set({ programs }),
    setSelectedProgram: (selectedProgram) => set({ selectedProgram }),
    setInvitationData: (invitationData) => handleSetInvitationData(set, invitationData),
    setRecipientEmail: (recipientEmail) => set({ recipientEmail }),
    updateInvitationMethods: (invitationMethods) =>
      set((state) => ({
        invitationData: state.invitationData
          ? { ...state.invitationData, invitationMethods }
          : null,
      })),
    setLoading: (loading) => set({ loading }),
    setError: (error) => set({ error }),
    setCurrentView: (currentView) => set({ currentView }),
    setInvitationChannelMode: (invitationChannelMode) => set({ invitationChannelMode }),
    setBulkProgress: (bulkProgress) => set({ bulkProgress }),
    setCollabType: (collabType) => set({ collabType }),
    preselectCollabTemplate: (collabTemplateId) => set({ collabTemplateId }),
    setCollabRecipientIdentityId: (collabRecipientIdentityId) => set({ collabRecipientIdentityId }),
    setCollabBrief: ({ mode, templateId, name, content, compensationConfirmed }) =>
      set({
        collabBriefMode: mode,
        collabTemplateId: templateId,
        collabName: name,
        collabContent: content,
        compensationConfirmed,
      }),
    updateCollabContent: (updater) =>
      set((state) => ({
        collabContent: state.collabContent ? updater(state.collabContent) : state.collabContent,
      })),
    setRespondByDays: (respondByDays) => set({ respondByDays }),
    setSaveAsTemplate: (saveAsTemplate) => set({ saveAsTemplate }),
    setCompensationConfirmed: (compensationConfirmed) => set({ compensationConfirmed }),
    setCollabDeliveryMode: (collabDeliveryMode) => set({ collabDeliveryMode }),
    setCollabSendResults: (collabSendResults) => set({ collabSendResults }),
    setCollabReproposedFromCollabId: (collabReproposedFromCollabId) =>
      set({ collabReproposedFromCollabId }),
    setCollabBriefApplicationId: (collabBriefApplicationId) => set({ collabBriefApplicationId }),
    setCollabReseedRecipient: (collabReseedRecipient) => set({ collabReseedRecipient }),
    setCollabRecipientDisplay: (collabRecipientDisplay) => set({ collabRecipientDisplay }),
    setCollabRecipientUgcOffer: (collabRecipientUgcOffer) => set({ collabRecipientUgcOffer }),
    updateEmailAttachmentDraft: (update) =>
      set((state) => ({ emailAttachmentDraft: update(state.emailAttachmentDraft) })),
    setMessage: (message) => set({ message }),
    setMessageSnapshot: (messageSnapshot) =>
      set((state) => (state.messageSnapshot === messageSnapshot ? state : { messageSnapshot })),
    setCompiledMessage: (compiledMessage) => set({ compiledMessage }),
    setOriginalTemplate: (originalTemplate) =>
      set((state) => (state.originalTemplate === originalTemplate ? state : { originalTemplate })),
    resetMessage: () =>
      set((state) => ({
        messageSnapshot: state.originalTemplate,
        compiledMessage: '',
      })),
    setAiPersonalize: (aiPersonalize) => set({ aiPersonalize }),
    setMessageLanguage: (messageLanguage) => set({ messageLanguage }),
    setSource: (source) => set({ source }),
    setSourceRecommendationId: (sourceRecommendationId) => set({ sourceRecommendationId }),
  }));

export type InvitationStoreApi = ReturnType<typeof createInvitationStore>;
